import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "rhombus-react";
import { push } from "connected-react-router";

import { fetchLinkedCreatives, downloadAssets, fetchDynamicVariables } from "reducers/Assets";
import { confirmDeleteAsset } from "reducers/Confirm";
import { openModal } from "reducers/Modal";
import { addNotification } from "reducers/Notifications";

import ActionMenu, { BasicAction } from "widgets/ActionMenu";
import { ALWAYS_DISPLAY, buildList } from "widgets/ActionMenu/utils";
import { DownloadAssets } from "widgets/messages/DownloadAssets";
import ImposiumLink from "widgets/actions/ImposiumAction";

import { ASSET_TYPES, MODAL_TYPES, IMPOSIUM_URL, CV, CREATIVE_VIEW } from "utils/constants";
import { copyToClipboard, hasPermission } from "utils/common";
import { isAfterEffectsAsset, isImposiumProject, isVideoAsset, canTranscode, isTextAsset } from "utils/Assets";
import useNotifier from "hooks/useNotifier";

export const copyToClipboardAction = {
	name: 'copy',
	shouldDisplay: ({ dependentValues }) => isTextAsset(dependentValues.assetType),
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();

		const onCopy = () => {
			copyToClipboard(dependentValues.name)
				.then(() => {
					dispatch(addNotification({
						type: "success",
						messageHeader: "Text copied",
						message: "Successfully copied text to clipboard",
					}));
				});
		};

		return (
			<Icon
				data-tip="Copy to Clipboard"
				name="far fa-copy"
				className="bounce-circle"
				onClick={onCopy}
			/>
		);
	},
};

export const copyManifestRefToClipboardAction = {
	name: 'copyManifestRef',
	shouldDisplay: ({ dependentValues: { assetType, encoded } }) => isVideoAsset(assetType) && encoded,
	component: ({ dependentValues: { name }, libraryId }) => {
		const dispatch = useDispatch();

		const copyManifestRef = () => {
			let manifestRef = `${libraryId}/${name}`;
			copyToClipboard(manifestRef)
				.then(() => {
					dispatch(addNotification({
						type: "success",
						messageHeader: "Manifest reference copied",
						message: "Successfully copied manifest reference to clipboard",
					}));
				});
		};

		return (
			<Icon
				data-tip="Copy Manifest Reference to Clipboard"
				name="far fa-copy"
				className="bounce-circle"
				onClick={copyManifestRef}
			/>
		);
	},
};

export const deleteAssetAction = {
	name: 'delete',
	shouldDisplay: ALWAYS_DISPLAY,
	component: ({ libraryId, dependentValues, iconOnly = true }) => {
		let dispatch = useDispatch();

		function onDeleteAssets(libraryId, dependentValues){
			dispatch(confirmDeleteAsset(libraryId, dependentValues.assetType, [dependentValues]));
		}

		function handleClick() {
			onDeleteAssets(libraryId, dependentValues);
		}

		if (iconOnly) {
			return (
				<Icon
					data-tip="Delete Asset"
					name="far fa-trash-alt"
					className="delete-asset bounce-circle"
					onClick={handleClick}
				/>
			);
		} else {
			return (
				<BasicAction
					iconName="fas fa-trash-alt"
					name="Delete Asset"
					handleClick={handleClick}
				/>
			);
		}
	},
};

export const downloadAssetAction = {
	name: 'download',
	shouldDisplay: ({ dependentValues }) => {
		return !isTextAsset(dependentValues.assetType) && !isImposiumProject(dependentValues.assetType) && !dependentValues.encoded;
	},
	component: ({ libraryId, libraryName, dependentValues }) => {
		let dispatch = useDispatch()
			, notifier = useNotifier()
			, { assetType, id, name } = dependentValues;
		let fileName = name;

		if(assetType === ASSET_TYPES.RICHLOAD) {
			fileName = `${fileName.replace('/index.html', '')}.zip`;
		}

		return (
			<Icon
				data-tip="Download"
				name="far fa-cloud-download"
				className="bounce-circle"
				onClick={() => {
					const DOWNLOAD_TOPIC = "Download Assets";
					const message = {
						origin: "HOPI",
						task: "Download Started",
						status: "In Progress",
						data: {
							fileName,
							libraryName,
							assetType,
						},
						component: DownloadAssets,
					};

					notifier(
						dispatch(downloadAssets(libraryId, [id], assetType.toLowerCase(), fileName)),
						DOWNLOAD_TOPIC,
						message
					);
				}}
			/>
		);
	},
};

export const openTranscodeVideoModalAction = {
	name: 'transcode',
	shouldDisplay: ({ dependentValues }) => canTranscode(dependentValues),
	component: ({ dependentValues }) => {
		let dispatch = useDispatch();

		function openTranscodeVideoModal(id) {
			dispatch(push(`modal/transcode/${id}`));
		}

		return (
			<Icon
				data-tip="Transcode"
				name="fas fa-film"
				className="bounce-circle"
				onClick={() => openTranscodeVideoModal(dependentValues.id)}
			/>
		);
	},
};

export const previewAssetAction = {
	shouldDisplay: ({ dependentValues }) => {
		const { assetType } = dependentValues;
		return !(isTextAsset(assetType) || isAfterEffectsAsset(assetType));
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();

		function openPreviewBlade() {
			dispatch(openModal(MODAL_TYPES.PREVIEW_ASSET, { asset: dependentValues }));
		}

		return (
			<Icon
				data-tip="Preview"
				name="far fa-eye"
				className="bounce-circle"
				onClick={() => openPreviewBlade()}
			/>
		);
	},
};

export const showLinkedCreativesAction = {
	name: 'show-linked',
	shouldDisplay: ({ dependentValues }) => !isTextAsset(dependentValues.assetType),
	component: ({ libraryId, dependentValues }) => {
		let dispatch = useDispatch();

		function showLinkedCreatives(libraryId, dependentValues){
			dispatch(openModal(MODAL_TYPES.ASSET_LINKED_CREATIVES));
			dispatch(fetchLinkedCreatives(libraryId, dependentValues));
		}
		return (
			<Icon
				data-tip="Show Linked Creatives"
				name="far fa-link"
				className="bounce-circle"
				onClick={() => showLinkedCreatives(libraryId, dependentValues)}
			/>
		);
	},
};

export const dynamicVariablesAction = {
	name: "dynamic-variables",
	shouldDisplay: ({ dependentValues }) => isAfterEffectsAsset(dependentValues.assetType) || isImposiumProject(dependentValues.assetType),
	component: ({ libraryId, dependentValues = {} }) => {
		const { id: assetId, name, assetType } = dependentValues;
		const dispatch = useDispatch();

		const openDynamicVariables = ()  => {
			dispatch(openModal(MODAL_TYPES.ASSET_DYNAMIC_VARIABLES));
			dispatch(fetchDynamicVariables(libraryId, assetId, name, assetType));
		};

		return (
			<BasicAction
				name="Dynamic Variables"
				iconName="fas fa-bolt"
				handleClick={openDynamicVariables}
			/>
		);
	},
};

export const viewImposiumCompositionsAction = {
	name: "view-imposium-compositions",
	shouldDisplay: ({ dependentValues }) => isImposiumProject(dependentValues.assetType),
	component: ({ libraryId, dependentValues = {} }) => {
		const dispatch = useDispatch();
		const onClick = ()  => {
			dispatch(openModal(MODAL_TYPES.IMPOSIUM_COMPOSITIONS, { projectId: dependentValues.id, projectName: dependentValues.name }));
		};

		return (
			<BasicAction
				name="View Compositions"
				iconName="fas fa-photo-film"
				handleClick={onClick}
			/>
		);
	},
};

export const duplicateImposiumProjectAction = {
	name: "duplicate-imposium-project",
	shouldDisplay: ({ user, dependentValues }) => {
		return isImposiumProject(dependentValues.assetType) && hasPermission(user, CV, CREATIVE_VIEW.DUPLICATE);
	},
	component: ({ libraryId, dependentValues = {} }) => {
		const dispatch = useDispatch();
		const onClick = ()  => {
			dispatch(openModal(MODAL_TYPES.DUPLICATE_IMPOSIUM_PROJECT, { projectId: dependentValues.id, projectName: dependentValues.name }));
		};

		return (
			<BasicAction
				name="Duplicate Project"
				iconName="fas fa-clone"
				handleClick={onClick}
			/>
		);
	},
};

export const openInImposiumAction = {
	name: "open-in-imposium",
	shouldDisplay: ({ dependentValues }) => isImposiumProject(dependentValues.assetType),
	component: ({ libraryId, dependentValues = {} }) => {
		let imposiumUrl = `${IMPOSIUM_URL}/${dependentValues?.id}?organization_id=${dependentValues?.organizationId}`;
		return (
			<ImposiumLink
				key="imposium"
				url={imposiumUrl}
				tooltipContent="Open in Imposium"
				iconOnly={true}
			/>
		);
	},
};

export function AssetActions(props){
	let actions = [
		previewAssetAction,
		openTranscodeVideoModalAction,
		downloadAssetAction,
		openInImposiumAction,
		showLinkedCreativesAction,
		copyToClipboardAction,
		copyManifestRefToClipboardAction
	];
	let extraActions = [
		[dynamicVariablesAction, viewImposiumCompositionsAction, duplicateImposiumProjectAction],
		[deleteAssetAction],
	];
	const libraryProps = useSelector(state=> ({
		libraryId: state.libraryProperties.id,
		libraryName: state.libraryProperties.name,
	}));
	const user = useSelector(state => state.you);
	const { dependentValues } = props;

	const actionContext = { ...libraryProps, ...props, user };

	/**
	 * Text assets do not have an extra actions menu
	 */
	if (isTextAsset(dependentValues.assetType)) {
		actions = [
			...actions,
			deleteAssetAction,
		];
		extraActions = [];
	}

	let [menuItems] = buildList([actions], actionContext);

	return (
		<div className="grid-actions flex-center">
			{menuItems}
			<ActionMenu
				actionLists = {extraActions}
				actionContext = {{ ...actionContext, iconOnly: false }}
			/>
		</div>
	);
}

const AssetActionsCell = {
	key:"actions",
	name: "Actions",
	cellClass:"actions-cell",
	width:150,
	formatter: AssetActions,
	resizable:false,
	sortable:false,
	getRowMetaData:getActionMetadata,
};

function getActionMetadata(data){
	let extraInfo = {
		id: data.id,
		name:data.name,
		videoSource:data.videoSource,
		assetType: data.assetType,
		encoded:data.encoded,
		url:data.url,
		storyId: data.storyId,
		organizationId: data.organizationId,
	};

	return extraInfo;
}

export default AssetActionsCell;
